import React, { useContext, useEffect } from 'react';
import { useMenuItemDragAndDrop } from '@atlaskit/navigation-system/side-nav-items/drag-and-drop/use-menu-item-drag-and-drop';
import { ContainerAvatar as Avatar } from '@atlaskit/navigation-system';
import { NAV4_PROJECT_MENU_ITEM_DRAG_AND_DROP } from '../common/constants.tsx';
import type { DraggableProjectData } from '../common/types.tsx';
import { FocusRestorationContext } from './focus-restoration-context.tsx';

type UseProjectMenuItemDragAndDropArgs = {
	isEnabled?: boolean;
	projectId: string;
	projectKey: string;
	isFavourite: boolean | null | undefined;
	name: string;
	avatar: {
		large?: string;
	};
};

export const useProjectMenuItemDragAndDrop = (props: UseProjectMenuItemDragAndDropArgs) => {
	const focusRestorationContext = useContext(FocusRestorationContext);
	const { shouldRestoreFocusRef, clearRestoreFocus } = focusRestorationContext;
	const { projectId, projectKey, isFavourite, name, avatar, isEnabled = false } = props;
	/* isEnabled is used to determine if drag and drop functionality should be enabled – this is used to turn on the feature only for starred projects.
	By using this to pass undefined to draggable and dropTarget the useMenuItemDragAndDrop hook does not run its effects to attach event listeners.
	The drag and drop functionality is effectively disabled, while still setting up the refs which are required for consistent rendering. */
	const {
		state,
		draggableButtonRef,
		dropTargetRef,
		draggableAnchorRef,
		dragPreview,
		dropIndicator,
	} = useMenuItemDragAndDrop({
		draggable: isEnabled
			? {
					getInitialData: (): DraggableProjectData => ({
						id: projectId,
						key: projectKey,
						isFavourite,
						name,
						avatar: avatar.large || '',
						type: NAV4_PROJECT_MENU_ITEM_DRAG_AND_DROP,
					}),
					getDragPreviewPieces: () => ({
						elemBefore: <Avatar src={avatar.large || ''} />,
						content: name,
					}),
				}
			: undefined,
		dropTarget: isEnabled
			? {
					getData: (): DraggableProjectData => ({
						id: projectId,
						key: projectKey,
						isFavourite,
						name,
						avatar: avatar.large || '',
						type: NAV4_PROJECT_MENU_ITEM_DRAG_AND_DROP,
					}),
					getOperations: () => ({
						'reorder-after': 'available',
						'reorder-before': 'available',
					}),
					canDrop: ({ source }) => source.data.type === NAV4_PROJECT_MENU_ITEM_DRAG_AND_DROP,
				}
			: undefined,
	});

	useEffect(() => {
		if (shouldRestoreFocusRef.current && isEnabled) {
			// .focus() seems to be more reliable in the next task when used with VoiceOver
			setTimeout(() => {
				(draggableButtonRef.current ?? draggableAnchorRef.current)?.focus();
				clearRestoreFocus?.();
			});
		}
	}, [shouldRestoreFocusRef, clearRestoreFocus, draggableButtonRef, draggableAnchorRef, isEnabled]);

	return {
		state,
		draggableButtonRef,
		dropTargetRef,
		draggableAnchorRef,
		dragPreview: isEnabled ? dragPreview : null,
		dropIndicator: isEnabled ? dropIndicator : null,
		hasDragIndicator: isEnabled,
	};
};
